import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

export default function InhuurRespond() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reden, setReden] = useState('');
  const [showAfwijsForm, setShowAfwijsForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    fetch('/api/inhuur/public/' + token)
      .then(async r => {
        const body = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(body.error || 'Fout (' + r.status + ')');
        return body;
      })
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  async function respond(actie) {
    setSubmitting(true); setError('');
    try {
      const r = await fetch('/api/inhuur/public/' + token + '/respond', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ actie, reden: actie === 'afgewezen' ? reden : null })
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body.error || 'Fout (' + r.status + ')');
      setDone({ actie });
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  }

  const card = { maxWidth: 640, margin: '60px auto', padding: 32, background: '#fff', borderRadius: 12, boxShadow: '0 4px 24px rgba(0,0,0,0.08)', fontFamily: 'Arial, sans-serif' };
  const bigBtn = (color) => ({ flex: 1, padding: '16px 24px', background: color, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 'bold', fontSize: 16, cursor: submitting ? 'wait' : 'pointer', opacity: submitting ? 0.6 : 1 });

  if (loading) return (
    <div style={{ ...card, textAlign: 'center', color: '#6b7280' }}>
      <div style={{ fontSize: 40, marginBottom: 12 }}>⏳</div>
      Laden…
    </div>
  );

  if (error && !data) return (
    <div style={{ ...card, textAlign: 'center' }}>
      <div style={{ fontSize: 48, color: '#ef4444', marginBottom: 12 }}>⚠️</div>
      <h2 style={{ color: '#111', margin: '0 0 8px 0' }}>Link werkt niet</h2>
      <p style={{ color: '#6b7280' }}>{error}</p>
      <p style={{ color: '#9ca3af', fontSize: 13, marginTop: 24 }}>Neem contact op met de afzender voor een nieuwe link.</p>
    </div>
  );

  if (done) return (
    <div style={{ ...card, textAlign: 'center' }}>
      <div style={{ fontSize: 56, marginBottom: 12 }}>{done.actie === 'bevestigd' ? '✅' : '❌'}</div>
      <h2 style={{ color: '#111', margin: '0 0 8px 0' }}>Bedankt voor uw {done.actie === 'bevestigd' ? 'bevestiging' : 'reactie'}</h2>
      <p style={{ color: '#6b7280' }}>
        De aanvraag voor <strong>{data.resource_naam}</strong> is <strong>{done.actie}</strong>.
        De aanvrager is automatisch op de hoogte gebracht.
      </p>
      <p style={{ color: '#9ca3af', fontSize: 13, marginTop: 24 }}>U kunt deze pagina sluiten.</p>
    </div>
  );

  // Al gereageerd?
  if (data.response_actie) return (
    <div style={{ ...card, textAlign: 'center' }}>
      <div style={{ fontSize: 48, marginBottom: 12 }}>ℹ️</div>
      <h2 style={{ color: '#111', margin: '0 0 8px 0' }}>Reeds gereageerd</h2>
      <p style={{ color: '#6b7280' }}>
        Deze aanvraag is al <strong>{data.response_actie}</strong> op {new Date(data.response_op).toLocaleString('nl-NL')}.
      </p>
      {data.response_reden && <p style={{ color: '#6b7280', fontStyle: 'italic' }}>"{data.response_reden}"</p>}
    </div>
  );

  const fmtDate = (d) => new Date(d).toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const datumStr = data.datum_van === data.datum_tot ? fmtDate(data.datum_van) : fmtDate(data.datum_van) + ' t/m ' + fmtDate(data.datum_tot);

  return (
    <div style={card}>
      <h1 style={{ color: '#1e3a5f', borderBottom: '2px solid #1e3a5f', paddingBottom: 12, marginTop: 0 }}>
        📋 Inhuur-aanvraag
      </h1>
      <p style={{ color: '#6b7280', marginBottom: 24 }}>
        <strong>{data.bedrijf_naam}</strong> vraagt het volgende bij u op:
      </p>

      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 24 }}>
        <tbody>
          {[
            ['Resource', (data.resource_naam || '') + (data.leverancier ? ' (' + data.leverancier + ')' : '')],
            ['Aantal', data.aantal + ' ' + (data.eenheid || '')],
            data.specificatie ? ['Specificatie', data.specificatie] : null,
            ['Datum', datumStr],
            data.wo_ordernummer || data.werkorder_titel ? ['Werkorder', ((data.wo_ordernummer || '') + ' ' + (data.werkorder_titel || '')).trim()] : null,
            data.wo_locatie ? ['Locatie', data.wo_locatie] : null,
            data.contactpersoon_naam ? ['Werkvoorbereider', data.contactpersoon_naam + (data.contactpersoon_email ? ' (' + data.contactpersoon_email + ')' : '')] : null,
            data.monteur_naam ? ['Monteur ter plaatse', data.monteur_naam] : null,
            data.ordernummer ? ['Ordernummer', data.ordernummer] : null,
            data.opmerking ? ['Opmerking', data.opmerking] : null,
            data.besteller_naam ? ['Aanvrager', data.besteller_naam + (data.besteller_email ? ' (' + data.besteller_email + ')' : '')] : null
          ].filter(Boolean).map((r, i) => (
            <tr key={i}>
              <td style={{ padding: '8px 12px', background: '#f3f4f6', fontWeight: 'bold', width: 200, border: '1px solid #e5e7eb' }}>{r[0]}</td>
              <td style={{ padding: '8px 12px', background: '#fff', border: '1px solid #e5e7eb' }}>{r[1] || '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {error && <div style={{ padding: 10, background: '#fee2e2', border: '1px solid #ef4444', borderRadius: 6, marginBottom: 12 }}>⚠️ {error}</div>}

      {!showAfwijsForm ? (
        <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
          <button disabled={submitting} onClick={() => respond('bevestigd')} style={bigBtn('#10b981')}>
            ✅ Bevestigen
          </button>
          <button disabled={submitting} onClick={() => setShowAfwijsForm(true)} style={bigBtn('#ef4444')}>
            ❌ Afwijzen
          </button>
        </div>
      ) : (
        <div style={{ marginTop: 24, padding: 16, background: '#fef2f2', borderRadius: 8, border: '1px solid #fecaca' }}>
          <label style={{ display: 'block', fontWeight: 'bold', marginBottom: 8, color: '#7f1d1d' }}>
            Reden voor afwijzen (optioneel)
          </label>
          <textarea
            value={reden}
            onChange={e => setReden(e.target.value)}
            rows={3}
            placeholder="Bv. geen capaciteit op deze datum, prijsindicatie, andere datum mogelijk…"
            style={{ width: '100%', padding: 10, border: '1px solid #fca5a5', borderRadius: 6, fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box' }}
          />
          <div style={{ display: 'flex', gap: 12, marginTop: 12 }}>
            <button disabled={submitting} onClick={() => setShowAfwijsForm(false)} style={{ ...bigBtn('#9ca3af'), flex: 0, padding: '12px 20px' }}>
              Terug
            </button>
            <button disabled={submitting} onClick={() => respond('afgewezen')} style={bigBtn('#ef4444')}>
              ❌ Bevestig afwijzen
            </button>
          </div>
        </div>
      )}

      <p style={{ color: '#9ca3af', fontSize: 12, marginTop: 32, textAlign: 'center' }}>
        WOMSYS — beveiligde link, 14 dagen geldig
      </p>
    </div>
  );
}
